import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { ReactComponent as LockedIcon } from '../../../assets/icons/access/locked.svg';
import { ReactComponent as NoAccessIcon } from '../../../assets/icons/access/no-access.svg';
import AppCommunityAvatar from '../../../components/designe/app-entity-avatar/app-community-avatar/AppCommunityAvatar';
import AppButton, {
  AppEditButton,
} from '../../../components/designe/buttons/app-button/AppButton';
import {
  CancelSubscriptionRequestButton,
  RequestSubscriptionButton,
  SubscribeButton,
  UnsubscribeButton,
} from '../../../components/designe/buttons/subscription-buttons';
import PageContainer from '../../../components/layout/page-container/PageContainer';
import { useRegisterPageContainerError } from '../../../components/layout/page-container/page-container-error-handler';
import CommunityParticipantsCount from '../../../components/reusable/communities-list/CommunityParticipantsCount';
import LoadMoreFeedback from '../../../components/reusable/load-more-feedback/LoadMoreFeedback';
import NoResults from '../../../components/reusable/no-results';
import Posts from '../../../components/reusable/posts/Posts';
import UsersListSmall from '../../../components/reusable/users-list/UsersListSmall';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import withRouter from '../../../hoc/with-router';
import { AvatarSize, AvatarTypes } from '../../../shared/constants';
import { Paths } from '../../../shared/paths';
import CommunityProfileSkeleton from '../shared/community-profile-skeleton/CommunityProfileSkeleton';
import { useStyles } from './Community.styles';
import { useCommunityPage } from './useCommunityPage';

function Community(props) {
  const { params } = props;
  const {
    reviewedCommunity,
    posts,
    isRelationPanel,
    subscribers,
    isCommunityLoading,
    showNoResult,
    showNoDetailsAccess,
    showNoPostsAccess,
    onSubscribe,
    onUnsubscribe,
    isSubscribeLoading,
    isUnsubscribeLoading,
    showPrivateSubscribeAction,
    privatePostsSubscribeLabelKey,
    noAccessTitleKey,
    noAccessDescriptionKey,
    visibleRelationsCount,
    refetchAll,
    isFetchingPosts,
    postsError,
    pendingRequestsCount,
    pageContainerErrorState,
  } = useCommunityPage(params.communityId);
  const { t } = useTranslation();
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { navigate } = useNavigationStack();

  const subscriberCount =
    reviewedCommunity?.participantsCount ?? subscribers?.length ?? 0;

  function onEditClick() {
    navigate(
      `${Paths.communities.root}/${params.communityId}${Paths.communities.edit}`,
    );
  }

  function onRequestsClick() {
    navigate(
      `${Paths.communities.root}/${params.communityId}${Paths.communities.requests}`,
    );
  }

  useRegisterPageContainerError(pageContainerErrorState);

  if (isCommunityLoading) {
    return (
      <PageContainer
        backButton
        title={t('communities.community')}
        onRefresh={refetchAll}>
        <CommunityProfileSkeleton />
      </PageContainer>
    );
  }

  return (
    <PageContainer
      backButton
      title={t('communities.community')}
      onRefresh={refetchAll}>
      <div className={classes.communityInfoWrapper}>
        <div className={classes.avatar}>
          <AppCommunityAvatar
            community={reviewedCommunity}
            size={AvatarSize.l}
            type={AvatarTypes.round}
          />
        </div>
        <div className={classes.communityDetails}>
          <div className={classes.info}>
            <Typography variant="text-lg">
              {reviewedCommunity?.title}
            </Typography>
            {!showNoDetailsAccess && !!reviewedCommunity?.description && (
              <Typography variant="text-md" className={classes.description}>
                {reviewedCommunity.description}
              </Typography>
            )}
            {showNoDetailsAccess && (
              <Box className={classes.lockedProfileInfo}>
                <LockedIcon className={classes.lockedProfileIcon} />
                <Typography variant="text-md" className={classes.noAccessText}>
                  {t('communities.privateCommunityDetailsHidden')}
                </Typography>
              </Box>
            )}
            {!showNoDetailsAccess && (
              <CommunityParticipantsCount count={subscriberCount} />
            )}
          </div>
          <div className={classes.actions}>
            {reviewedCommunity?.permissions?.canSubscribe && (
              <SubscribeButton
                onClick={() => onSubscribe()}
                loading={isSubscribeLoading}
              />
            )}
            {reviewedCommunity?.permissions?.canRequestSubscription && (
              <RequestSubscriptionButton
                onClick={() => onSubscribe()}
                loading={isSubscribeLoading}
              />
            )}
            {reviewedCommunity?.permissions?.canUnsubscribe && (
              <UnsubscribeButton
                onClick={() => onUnsubscribe()}
                loading={isUnsubscribeLoading}
              />
            )}
            {reviewedCommunity?.permissions?.canCancelRequestSubscription && (
              <CancelSubscriptionRequestButton
                onClick={() => onUnsubscribe()}
                loading={isUnsubscribeLoading}
              />
            )}
            {reviewedCommunity?.permissions?.canSeeRequests && (
              <AppButton variant="contained" onClick={() => onRequestsClick()}>
                <Box className={classes.requestsButtonInner} component="span">
                  <span>{t('communities.requests')}</span>
                  {pendingRequestsCount > 0 && (
                    <Box
                      className={classes.requestCountBadge}
                      component="span"
                      aria-label={String(pendingRequestsCount)}>
                      {pendingRequestsCount > 99 ? '99+' : pendingRequestsCount}
                    </Box>
                  )}
                </Box>
              </AppButton>
            )}
            {reviewedCommunity?.permissions?.canEdit && (
              <AppEditButton onClick={() => onEditClick()}>
                {t('common.edit')}
              </AppEditButton>
            )}
          </div>
        </div>
      </div>
      {showNoPostsAccess && (
        <Box className={classes.privatePostsContainer}>
          <Box className={classes.privatePostsIconWrapper}>
            <NoAccessIcon className={classes.privatePostsIcon} />
          </Box>
          <Typography variant="h2" className={classes.privatePostsTitle}>
            {t(noAccessTitleKey)}
          </Typography>
          <Typography
            variant="text-md"
            className={classes.privatePostsDescription}>
            {t(noAccessDescriptionKey)}
          </Typography>
          {showPrivateSubscribeAction && (
            <SubscribeButton
              labelKey={privatePostsSubscribeLabelKey}
              onClick={() => onSubscribe()}
              loading={isSubscribeLoading}
            />
          )}
        </Box>
      )}
      {reviewedCommunity?.permissions?.canSeePosts && (
        <Box className={classes.communityDetailsWrapper}>
          <Box className={classes.communityPostsWrapper}>
            {showNoResult && <NoResults />}
            {!showNoResult && (
              <Posts
                posts={posts}
                isLoading={isFetchingPosts}
                refreshPosts={refetchAll}
                newPostBinding={{ communityId: reviewedCommunity?.id }}
                allowNewPost={reviewedCommunity?.permissions?.canAddPost}
              />
            )}
            <LoadMoreFeedback
              error={postsError}
              isFetching={Boolean(isFetchingPosts) && Boolean(posts?.length)}
              onRetry={refetchAll}
            />
            <Box mb={1} />
          </Box>
          {!!isRelationPanel && (
            <Box className={classes.communityRelationsWrapper}>
              {!!subscribers?.length && (
                <Box className={classes.communityRelationsContainer}>
                  <Link
                    className={classes.communityRelationsHeader}
                    to={Paths.communities.toCommunitySubscribers(
                      reviewedCommunity?.id,
                    )}>
                    <Typography variant="h3" marginBottom={1}>
                      {t('communities.communitySubscribers')}
                      <Typography
                        component="span"
                        className={classes.communityRelationsCounter}>
                        {subscriberCount}
                      </Typography>
                    </Typography>
                  </Link>
                  <UsersListSmall
                    users={subscribers.slice(0, visibleRelationsCount)}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </PageContainer>
  );
}

export default withRouter(Community);
